import { useCallback, useRef, useState } from "react";
import {
	areSnapshotsEqual,
	cloneSnapshot,
	createEmptySnapshot,
	type CetSnapshot,
	type DeliverableItem,
} from "./engagementTermsState";

export type EngagementTermsValidationField =
	| "startDate"
	| "endDate"
	| "currency"
	| "paymentTerms"
	| "amount"
	| "exclusivityPeriod";

export type EngagementTermsValidationErrors = Partial<Record<EngagementTermsValidationField, string>>;

export const useEngagementTermsForm = () => {
	const empty = createEmptySnapshot();
	const [startDate, setStartDate] = useState(empty.startDate);
	const [endDate, setEndDate] = useState(empty.endDate);
	const [currency, setCurrency] = useState(empty.currency);
	const [paymentTerms, setPaymentTerms] = useState(empty.paymentTerms);
	const [amount, setAmount] = useState(empty.amount);
	const [exclusivityEnabled, setExclusivityEnabled] = useState(empty.exclusivityEnabled);
	const [exclusivityPeriod, setExclusivityPeriod] = useState(empty.exclusivityPeriod);
	const [exclusivityCategory, setExclusivityCategory] = useState(empty.exclusivityCategory);
	const [brands, setBrands] = useState<string[]>(empty.brands);
	const [brandDraft, setBrandDraft] = useState("");
	const [validationErrors, setValidationErrors] = useState<EngagementTermsValidationErrors>({});
	const savedSnapshotRef = useRef<CetSnapshot>(cloneSnapshot(empty));

	const setFieldsFromSnapshot = useCallback((snapshot: CetSnapshot) => {
		setStartDate(snapshot.startDate);
		setEndDate(snapshot.endDate);
		setCurrency(snapshot.currency);
		setPaymentTerms(snapshot.paymentTerms);
		setAmount(snapshot.amount);
		setExclusivityEnabled(snapshot.exclusivityEnabled);
		setExclusivityPeriod(snapshot.exclusivityPeriod);
		setExclusivityCategory(snapshot.exclusivityCategory);
		setBrands([...snapshot.brands]);
		setBrandDraft("");
		setValidationErrors({});
	}, []);

	const clearValidationError = useCallback((field: EngagementTermsValidationField) => {
		setValidationErrors((current) => {
			if (!current[field]) return current;
			const next = { ...current };
			delete next[field];
			return next;
		});
	}, []);

	const hydrate = useCallback(
		(snapshot: CetSnapshot) => {
			const copy = cloneSnapshot(snapshot);
			savedSnapshotRef.current = copy;
			setFieldsFromSnapshot(copy);
		},
		[setFieldsFromSnapshot],
	);

	const commit = useCallback((snapshot: CetSnapshot) => {
		savedSnapshotRef.current = cloneSnapshot(snapshot);
	}, []);

	const reset = useCallback((): CetSnapshot => {
		const snapshot = cloneSnapshot(savedSnapshotRef.current);
		setFieldsFromSnapshot(snapshot);
		return snapshot;
	}, [setFieldsFromSnapshot]);

	const isDirty = useCallback(
		(snapshot: CetSnapshot): boolean => !areSnapshotsEqual(snapshot, savedSnapshotRef.current),
		[],
	);

	const toSnapshot = useCallback(
		(deliverables: DeliverableItem[]): CetSnapshot => ({
			startDate,
			endDate,
			currency,
			paymentTerms,
			amount,
			exclusivityEnabled,
			exclusivityPeriod,
			exclusivityCategory,
			brands,
			deliverables,
		}),
		[
			amount,
			brands,
			currency,
			endDate,
			exclusivityCategory,
			exclusivityEnabled,
			exclusivityPeriod,
			paymentTerms,
			startDate,
		],
	);

	const addBrand = useCallback(
		(brand: string) => {
			const normalized = brand.trim();
			if (!normalized || brands.some((item) => item.toLowerCase() === normalized.toLowerCase())) return;
			setBrands((current) => [...current, normalized]);
			setBrandDraft("");
		},
		[brands],
	);

	const removeBrand = useCallback((brand: string) => {
		setBrands((current) => current.filter((item) => item !== brand));
	}, []);

	const validate = useCallback(
		(requireAmount = false): EngagementTermsValidationErrors => {
			const errors: EngagementTermsValidationErrors = {};
			const normalizedAmount = amount.replace(/,/g, "");
			if (!startDate) errors.startDate = "Effective start date is required";
			if (!endDate) errors.endDate = "Effective end date is required";
			else if (startDate && endDate < startDate) errors.endDate = "End date must be after the start date";
			if (!currency) errors.currency = "Currency is required";
			if (!paymentTerms.trim()) errors.paymentTerms = "Payment terms are required";
			if (requireAmount && (!normalizedAmount || !/^\d+(?:\.\d{1,2})?$/.test(normalizedAmount))) {
				errors.amount = "Enter a valid creator fee amount";
			}
			if (exclusivityEnabled && !exclusivityPeriod) {
				errors.exclusivityPeriod = "Exclusivity period is required";
			}
			setValidationErrors(errors);
			return errors;
		},
		[amount, currency, endDate, exclusivityEnabled, exclusivityPeriod, paymentTerms, startDate],
	);

	return {
		startDate,
		setStartDate,
		endDate,
		setEndDate,
		currency,
		setCurrency,
		paymentTerms,
		setPaymentTerms,
		amount,
		setAmount,
		exclusivityEnabled,
		setExclusivityEnabled,
		exclusivityPeriod,
		setExclusivityPeriod,
		exclusivityCategory,
		setExclusivityCategory,
		brands,
		brandDraft,
		setBrandDraft,
		addBrand,
		removeBrand,
		validationErrors,
		clearValidationError,
		hydrate,
		commit,
		reset,
		isDirty,
		toSnapshot,
		validate,
	};
};
